import { useEffect } from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { StepProps, track, useAutoNavigate } from '@betterment/js-runtime';
import { Box, Button } from '@betterment/samba-react';

import StepHeading from '../../AccountCreationFlow/components/StepHeading';
import { SecuritiesLendingStatus } from '../../graphql/schema';
import FullPageLoader from '../../Shared/components/FullPageLoader';
import {
  SecLending,
  hasEnabledSecLending,
} from '../../Shared/components-v2/user-level-settings/components/SettingsControls';
import UserLevelSettings from '../../Shared/components-v2/user-level-settings/UserLevelSettings';
import { WorkflowLayout } from '../../Shared/layout/WorkflowLayout';
import { UserLevelSettingsDocument } from '../graphql/query.userLevelSettings.generated';
import { useEditUserLevelSettings } from '../hooks/useEditUserLevelSettings';

export const UpdateUserLevelSettingsStep: React.FC<StepProps> = ({ goToNextStep, flowState }) => {
  const navigate = useAutoNavigate();

  const { data } = useGraphQLQuery(UserLevelSettingsDocument, { throwOnError: true, fetchPolicy: 'network-only' });

  const { handleUpdate, error, loading } = useEditUserLevelSettings(flowState.returnToPath);

  useEffect(() => {
    if (error) throw new Error('Error while updating user level settings');
  }, [error]);

  if (!data) return <FullPageLoader />;

  const isSecLendingSelected = data.userLevelSettings.securityLending?.status === SecuritiesLendingStatus.Enrolled;
  const handleClose = () => navigate(flowState.returnToPath);
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isSecLendingSelected === hasEnabledSecLending(event)) {
      void handleClose();
      return;
    }

    if (hasEnabledSecLending(event)) {
      goToNextStep();
      return;
    }

    track('ElementClicked', {
      name: 'SecLendingUnEnrolled',
      flow: 'EditUserLevelSettings',
    });
    await handleUpdate(false);
  };

  return (
    <WorkflowLayout
      feature="update_user_level_settings"
      pageTitle="Update User Level Setting"
      workflowTitle=""
      onClose={handleClose}
    >
      <Box css={{ display: 'grid', gap: 'space.300', marginY: 'space.300' }}>
        <StepHeading>Portfolio controls</StepHeading>
        <form onSubmit={handleSubmit}>
          <UserLevelSettings controls={[SecLending(isSecLendingSelected)]} />
          <Box
            css={{
              display: 'flex',
              flexDirection: 'row',
              gap: 'space.100',
              smDown: {
                display: 'flex',
                flexDirection: 'column',
                position: 'fixed',
                bottom: 'space.0',
                left: 'space.0',
                right: 'space.0',
                justifyContent: 'center',
                backgroundColor: 'background.surface',
                paddingY: 'space.100',
                paddingX: 'space.300',
              },
            }}
          >
            <Box css={{ width: '1/4', smDown: { width: '100%' } }}>
              <Button type="submit" variant="solidPrimary" css={{ width: '100%' }}>
                Save
              </Button>
            </Box>
            <Box css={{ width: '1/4', smDown: { width: '100%' } }}>
              <Button
                type="button"
                onClick={handleClose}
                loading={loading}
                variant="solidSecondary"
                css={{ width: '100%' }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </form>
      </Box>
    </WorkflowLayout>
  );
};
